/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import {useDispatch} from 'react-redux';
import {ColDef} from 'ag-grid-community';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {loadEventHistory} from '@ion/core/src/services/Connect';
import {Device, GenericObject} from '@ion/core/src/types/entities';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {clone} from '@ion/core/src/utils/ObjectUtil';
import {multiLevelSort} from '@ion/core/src/utils/SortUtils';
import {capitalizeFirstLetter} from '@ion/core/src/utils/StringUtil';
import {resolveDefinitions} from '@ion/dls/src/grid/ColumnResolver';
import {
  IonGrid,
  IonGridAPI
} from '@ion/dls/src/grid/IonGrid';
import {getSortModel, SortModelType} from '@ion/dls/src/grid/IonGridUtils';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import {exportEventHistory} from '../../store/actions/exporter';
import {EventRowEditor} from './EventRowEditor';
import {useStyles} from './FileHistoryGrid';

export interface EventsGridProps {
  device?: Device;
  eventTime?: any;
  eventType?: string;
  refreshGrid?: boolean;
};

/**
 * Grid to be rendered on the File Tab under Device Details page
 */
export const EventsGrid = ({
  device,
  eventTime,
  eventType,
  refreshGrid
}: EventsGridProps, ref) => {
  const classes                               = useStyles();
  const dispatch                              = useDispatch();
  const ionGridRef                            = useRef<IonGridAPI>();
  const [gridApi, setGridApi]                 = useState(null);
  const [hasItems, setHasItems]               = useState<boolean>(false);
  const [refreshRequired, setRefreshRequired] = useState(false);
  const [totalCount, setTotalCount]           = useState(null);

  /**
   * Returns a reference to the grids public api ..
   */
  useImperativeHandle(ref, () => ({
    onExport
  }));

  const onExport = function() {
    const params = ionGridRef.current?.getExportParams(false);
    const searchParams = {
      deviceId  : device?.id,
      eventTime : eventTime,
      msgType   : eventType,
      pageSize  : totalCount
    };
    dispatch(exportEventHistory(params, searchParams));
  };

  /**
   * Takes a list of Event history and constructs a list that
   * can be easily consumed by the IonGrid API
   */
  const constructEventsGridRows = useCallback(function(events: GenericObject[]): GridResult[] {
    const gridItems = events?.map(event => {
      const [key, value] = Object.entries(event)[0];
      const {eventTime}  = value || {};
      return {
        ...value,
        eventTime   : eventTime,
        eventNameUI : capitalizeFirstLetter(key),
        eventTimeUI : formatDateTime(eventTime)
      };
    });
    setHasItems(!!gridItems.length);
    const sortModel = getSortModel(gridApi)?.sort || 'desc';
    return multiLevelSort({items: gridItems, property1: 'eventTime', property2: 'deviceMessageSequenceNumber', order: sortModel as string}); ;
  }, [gridApi]);

  // Provides reference to the grid's api.
  const onGridReady = useCallback(function(api) {
    setGridApi(api);
  }, []);

  const defaultSort: SortModelType = {
    colId : 'eventTime',
    sort  : 'desc'
  };

  const attributeMappings = {
    eventTimeUI : 'eventTime'
  };

  /**
   * Returns a reference to the grids public api ..
   */
  const getEventHistory = async function({
    page,
    pageSize,
    sort,
    callback
  }) {
    try {
      if (!isNil(eventTime)) {
        const {events, totalCount} = await loadEventHistory({
          deviceId  : device?.id,
          eventTime : eventTime,
          msgType   : eventType,
          pageId    : page,
          pageSize  : pageSize,
          sortOn    : sort
        });
        const eventHistory = clone(events);
        const results      = {items: eventHistory, totalCount: totalCount};
        setTotalCount(totalCount);
        callback(results);
        return {eventHistory, totalCount};
      }
    } catch (err) {
      error('Error occurred retrieving detailed device event data.');
    }
  };

  useEffect(() => {
    gridApi?.hideOverlay();
    if (gridApi && !hasItems) {
      gridApi.showNoRowsOverlay();
    }
  }, [hasItems, gridApi]);

  useEffect(() => {
    if (refreshGrid) {
      setRefreshRequired(true);
      setTimeout(() => {
        setRefreshRequired(false);
      }, 500);
    }
  }, [refreshGrid]);

  const getGridHeight = useCallback(function() {
    return classes.filesGrid;
  }, []);

  return (
    <IonGrid omitHeader
      ref={ionGridRef}
      paginationEnabled
      showCustomPaginationBar
      loadItems={getEventHistory}
      augmentItems={constructEventsGridRows}
      attributeMappings={attributeMappings}
      columnDefinitions={getColumnDefinitions()}
      defaultSort={defaultSort}
      refreshRequired={refreshRequired}
      setRefreshRequired={setRefreshRequired}
      checkboxesEnabled={false}
      showHeaderCheckbox={false}
      viewSubGroups={false}
      getGridHeight={getGridHeight}
      onGridReady={onGridReady}
      columnComponents={{
        rowMenu : item => (
          <EventRowEditor event={item?.data} />
        )
      }}
      pageOffset
      pageSizes={PAGE_SIZES} />
  );
};

/**
 * Returns column definitions for desktop event history grid
 */
const getColumnDefinitionsDesktop = (): ColDef[] => {
  return [{
    headerName : translate('admin-devices:view.events.grid.eventName'),
    field      : 'eventNameUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.events.grid.eventType'),
    field      : 'eventType',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.events.grid.deviceEsn'),
    field      : 'deviceEsn',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.events.grid.messageUuid'),
    field      : 'messageUuid',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.events.grid.eventTime'),
    field      : 'eventTimeUI',
    sortable   : true,
    sort       : 'desc',
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  }];
};

/**
 * Returns column definitions for mobile event history grid
 */
const getColumnDefinitionsMobile = (): ColDef[] => {
  return [{
    headerName : translate('admin-devices:view.events.grid.eventName'),
    field      : 'eventNameUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.events.grid.eventType'),
    field      : 'eventType',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.events.grid.deviceEsn'),
    field      : 'deviceEsn',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.events.grid.messageUuid'),
    field      : 'messageUuid',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.events.grid.eventTime'),
    field      : 'eventTimeUI',
    sortable   : true,
    sort       : 'desc',
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  }];
};
const getColumnDefinitions = () => ({
  desktop : resolveDefinitions(getColumnDefinitionsDesktop()),
  mobile  : resolveDefinitions(getColumnDefinitionsMobile())
});
export default forwardRef(EventsGrid);
